import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { vertriebAPI } from '@/lib/api';
import { toast } from 'sonner';
import {
  PhoneCall, PhoneMissed, Voicemail, MessageCircle, RefreshCw, Loader2,
  Clock, CheckCircle2, CalendarClock, Gauge, ChevronRight, X,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

/* Setter-Cockpit — "Vertrieb heute" (Paket P1 aus dem Setter-Closer-Playbook).
 *
 * Eine Arbeitsliste, keine Auswertung: oben die Tageszahlen, darunter erst
 * die NEUEN Leads (Speed-to-Lead!), dann die fälligen Wiedervorlagen.
 * Jeder Versuch wird per Klick protokolliert — die Kadenz (Tag 0 zweimal,
 * Tag 1, Tag 3, Tag 7, dann Nachfassen) rechnet der Server.
 *
 * "Erreicht" öffnet den Qualifizierungs-Button-Baum: vier Fragen, vier
 * Klickreihen, bewusst ohne Freitext-Zwang (Betreiber-Entscheidung:
 * deterministischer Baum statt KI-Dialog).
 */

const AMPEL = {
  gruen: { klasse: 'bg-emerald-100 text-emerald-700', label: 'im Ziel' },
  gelb: { klasse: 'bg-amber-100 text-amber-700', label: 'heute retten' },
  rot: { klasse: 'bg-red-100 text-red-700', label: 'überfällig' },
};

const OPTION_LABEL = {
  kind: 'Kind', erwachsener: 'Erwachsener', frau: 'Frau',
  fitness: 'Fitness', selbstverteidigung: 'Selbstverteidigung', wettkampf: 'Wettkampf',
  abnehmen: 'Abnehmen', ausgleich: 'Ausgleich', sonstiges: 'Sonstiges',
  vormittags: 'Vormittags', nachmittags: 'Nachmittags', abends: 'Abends',
  wochenende: 'Wochenende', flexibel: 'Flexibel',
  sofort: 'Sofort', diesen_monat: 'Diesen Monat', unverbindlich: 'Unverbindlich',
  macht_es: 'Zieht es durch', unsicher: 'Unsicher', eher_nicht: 'Eher nicht',
  selbst: 'Entscheidet selbst', partner: 'Partner entscheidet mit', eltern: 'Eltern entscheiden',
};

/* FBC-Setting (25.09.2026): Motiv (Ziel), Macher, Zeit, Entscheider + Dringlichkeit */
const FRAGEN = [
  { key: 'fuer_wen', titel: 'Für wen?' },
  { key: 'ziel', titel: 'Motiv — warum jetzt?' },
  { key: 'macher', titel: 'Macher — zieht die Person es durch?' },
  { key: 'zeitfenster', titel: 'Zeit — wann verfügbar?' },
  { key: 'entscheider', titel: 'Entscheider — wer entscheidet?' },
  { key: 'dringlichkeit', titel: 'Wie dringend?' },
];

function alterText(min) {
  if (min == null) return '—';
  if (min < 60) return `vor ${min} min`;
  if (min < 60 * 24) return `vor ${Math.floor(min / 60)} h`;
  return `vor ${Math.floor(min / (60 * 24))} Tagen`;
}

export default function VertriebPage() {
  const [daten, setDaten] = useState(null);
  const [laedt, setLaedt] = useState(true);
  const [sende, setSende] = useState(null);          // lead_id, dessen Buttons gerade sperren
  const [quali, setQuali] = useState(null);          // { lead } -> Dialog offen
  const [antworten, setAntworten] = useState({});
  const [qualiNotiz, setQualiNotiz] = useState('');

  const laden = useCallback(async () => {
    try {
      const res = await vertriebAPI.cockpit();
      setDaten(res.data);
    } catch {
      toast.error('Cockpit konnte nicht geladen werden');
    } finally {
      setLaedt(false);
    }
  }, []);

  useEffect(() => { laden(); }, [laden]);
  // Die Liste lebt: alle 60 s neu laden, damit die Ampeln aktuell bleiben.
  useEffect(() => {
    const t = setInterval(laden, 60000);
    return () => clearInterval(t);
  }, [laden]);

  const versuch = async (lead, ergebnis) => {
    setSende(lead.id);
    try {
      const res = await vertriebAPI.versuch(lead.id, { ergebnis });
      if (ergebnis === 'erreicht') {
        setQuali({ lead });
        setAntworten({});
        setQualiNotiz('');
        toast.success('Erreicht — jetzt qualifizieren');
      } else {
        const nv = res.data.naechster_versuch;
        toast.success(`Protokolliert — Wiedervorlage ${nv ? new Date(nv).toLocaleString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : 'gesetzt'}`);
      }
      laden();
    } catch {
      toast.error('Versuch konnte nicht gespeichert werden');
    } finally {
      setSende(null);
    }
  };

  const qualiSpeichern = async () => {
    const fehlt = FRAGEN.find((f) => !antworten[f.key]);
    if (fehlt) { toast.error(`Bitte beantworten: ${fehlt.titel}`); return; }
    try {
      await vertriebAPI.qualifizierung(quali.lead.id, { ...antworten, notiz: qualiNotiz });
      toast.success('Qualifizierung gespeichert — jetzt Probetraining buchen');
      setQuali(null);
      laden();
    } catch {
      toast.error('Qualifizierung konnte nicht gespeichert werden');
    }
  };

  if (laedt) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }
  if (!daten) return null;
  const k = daten.kennzahlen;

  const Zeile = ({ lead, faellig }) => {
    const ampel = AMPEL[lead.ampel] || AMPEL.rot;
    const gesperrt = sende === lead.id;
    return (
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-border/50 last:border-b-0" data-testid={`vertrieb-lead-${lead.id}`}>
        <div className="min-w-[180px] flex-1">
          <div className="font-bold text-sm flex items-center gap-2">
            {lead.name || 'Ohne Namen'}
            {lead.qualifizierung && <Badge variant="secondary" className="text-[10px]">qualifiziert</Badge>}
            {lead.probetraining && <Badge variant="secondary" className="text-[10px] bg-emerald-50 text-emerald-700">Probetraining gebucht</Badge>}
          </div>
          <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
            <span>{lead.quelle || 'ohne Quelle'}</span>
            {faellig
              ? <span className="flex items-center gap-1"><Clock className="w-3 h-3" />Wiedervorlage fällig · Versuch {lead.versuche + 1}</span>
              : <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${ampel.klasse}`}>{alterText(lead.minuten_seit_eingang)} · {ampel.label}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {lead.telefon && (
            <a href={`tel:${lead.telefon}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-marke text-white text-xs font-bold hover:bg-marke-dunkel" data-testid="btn-anrufen">
              <PhoneCall className="w-3.5 h-3.5" /> {lead.telefon}
            </a>
          )}
          {lead.whatsapp_link && (
            <a href={lead.whatsapp_link_vorlage || lead.whatsapp_link} target="_blank" rel="noreferrer" className="inline-flex items-center px-2.5 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-muted" title="WhatsApp mit fertigem Text öffnen — vor dem Senden anpassbar">
              <MessageCircle className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <Button size="sm" variant="outline" disabled={gesperrt} onClick={() => versuch(lead, 'erreicht')} className="text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid="btn-erreicht">
            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Erreicht
          </Button>
          <Button size="sm" variant="outline" disabled={gesperrt} onClick={() => versuch(lead, 'nicht_erreicht')} data-testid="btn-nicht-erreicht">
            <PhoneMissed className="w-3.5 h-3.5 mr-1" /> Nicht erreicht
          </Button>
          <Button size="sm" variant="ghost" disabled={gesperrt} onClick={() => versuch(lead, 'mailbox')} title="Mailbox besprochen" data-testid="btn-mailbox">
            <Voicemail className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-5" data-testid="vertrieb-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold flex items-center gap-2"><PhoneCall className="w-6 h-6 text-marke" /> Vertrieb heute</h1>
          <p className="text-sm text-muted-foreground">Erst die Neuen (Ziel: Anruf in {daten.sla.gruen_bis} Minuten), dann die Wiedervorlagen.</p>
          <p className="text-[11px] mt-0.5">
            {daten.automatik_whatsapp
              ? <span className="text-emerald-600">Probetraining-Bestätigungen gehen automatisch per WhatsApp raus. Ausnahmen warten mit Hinweis in der Freigabe-Zentrale.</span>
              : <span className="text-amber-600">Probetraining-Bestätigungen warten in der Freigabe-Zentrale, bis sie jemand sendet.</span>}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={laden}><RefreshCw className="w-4 h-4 mr-1" /> Aktualisieren</Button>
      </div>

      {/* Tageszahlen */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4">
          <div className="text-xs text-muted-foreground flex items-center gap-1"><PhoneCall className="w-3.5 h-3.5" /> Neue Leads offen</div>
          <div className="text-2xl font-extrabold">{k.offen_neu}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Wiedervorlagen fällig</div>
          <div className="text-2xl font-extrabold">{k.wiedervorlagen_faellig}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-muted-foreground flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Heute erreicht / Versuche</div>
          <div className="text-2xl font-extrabold">{k.erreicht_heute}<span className="text-base font-semibold text-muted-foreground"> / {k.versuche_heute}</span></div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-muted-foreground flex items-center gap-1"><Gauge className="w-3.5 h-3.5" /> Speed-to-Lead heute</div>
          <div className="text-2xl font-extrabold">{k.speed_to_lead_schnitt != null ? `${k.speed_to_lead_schnitt} min` : '—'}</div>
        </CardContent></Card>
      </div>

      {/* Neue Leads */}
      <Card>
        <CardContent className="p-0">
          <div className="px-4 py-3 border-b border-border/60 flex items-center justify-between">
            <h2 className="text-sm font-bold">Jetzt anrufen — neue Leads</h2>
            <span className="text-xs text-muted-foreground">älteste zuerst</span>
          </div>
          {daten.neue.length === 0
            ? <div className="px-4 py-8 text-center text-sm text-muted-foreground">Keine offenen neuen Leads — stark. 💪</div>
            : daten.neue.map((l) => <Zeile key={l.id} lead={l} />)}
        </CardContent>
      </Card>

      {/* Wiedervorlagen */}
      <Card>
        <CardContent className="p-0">
          <div className="px-4 py-3 border-b border-border/60">
            <h2 className="text-sm font-bold">Wiedervorlagen fällig</h2>
          </div>
          {daten.wiedervorlagen.length === 0
            ? <div className="px-4 py-8 text-center text-sm text-muted-foreground">Nichts fällig.</div>
            : daten.wiedervorlagen.map((l) => <Zeile key={l.id} lead={l} faellig />)}
        </CardContent>
      </Card>

      {/* Qualifizierungs-Dialog: der Button-Baum */}
      {quali && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setQuali(null)}>
          <div className="bg-background rounded-2xl w-full max-w-lg p-5 space-y-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="quali-dialog">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold">{quali.lead.name || 'Lead'} qualifizieren</h3>
                <p className="text-xs text-muted-foreground">Motiv, Macher, Zeit, Entscheider (FBC) — dann Probetraining buchen und das Abschlussgespräch danach ankündigen.</p>
              </div>
              <button onClick={() => setQuali(null)} className="p-1 rounded hover:bg-muted"><X className="w-4 h-4" /></button>
            </div>

            {FRAGEN.map((f) => (
              <div key={f.key}>
                <div className="text-[11px] font-medium text-muted-foreground mb-1.5">{f.titel}</div>
                <div className="flex flex-wrap gap-2">
                  {(daten.optionen[f.key] || []).map((opt) => (
                    <button key={opt} onClick={() => setAntworten((a) => ({ ...a, [f.key]: opt }))}
                      className={`px-3 py-2 rounded-xl border-2 text-xs font-bold transition-all ${antworten[f.key] === opt ? 'border-marke bg-marke/5 text-marke' : 'border-border/60 text-muted-foreground hover:border-border'}`}
                      data-testid={`quali-${f.key}-${opt}`}>
                      {OPTION_LABEL[opt] || opt}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            <div>
              <div className="text-[11px] font-medium text-muted-foreground mb-1.5">Notiz (optional)</div>
              <textarea value={qualiNotiz} onChange={(e) => setQualiNotiz(e.target.value)} rows={2}
                className="w-full rounded-xl border border-border bg-background p-2.5 text-sm" placeholder="z. B. Kind 8 Jahre, Freundin trainiert schon hier" />
            </div>

            <div className="flex items-center gap-2 pt-1">
              <Button onClick={qualiSpeichern} className="flex-1 bg-marke hover:bg-marke-dunkel" data-testid="quali-speichern">
                Speichern
              </Button>
              <Button variant="outline" asChild>
                <Link to="/probetrainings"><CalendarClock className="w-4 h-4 mr-1" /> Probetraining buchen <ChevronRight className="w-3.5 h-3.5" /></Link>
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground">Ansage am Telefon: „Nach dem Training setzen wir uns 15 Minuten zusammen und schauen, ob und wie es passt."</p>
          </div>
        </div>
      )}
    </div>
  );
}
